import React, { useState, useEffect, useMemo } from "react";
import { fetchWithAuth } from "../utils/fetchWithAuth";
import {
  Box,
  VStack,
  HStack,
  Button,
  Text,
  Textarea,
  Input,
  Select,
  Alert,
  AlertIcon,
  Code,
  Badge,
} from "@chakra-ui/react";

const EndpointTester = () => {
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedEndpoint, setSelectedEndpoint] = useState("/chat");
  const [requestBody, setRequestBody] = useState("");
  const [method, setMethod] = useState("POST");

  const testData = useMemo(
    () => ({
      // AI endpoints (use /api/ai/ prefix)
      "/api/ai/ping": "",
      "/api/ai/echo": JSON.stringify(
        {
          test_message: "Hello, this is a test echo",
          timestamp: new Date().toISOString(),
        },
        null,
        2,
      ),
      "/api/chat": JSON.stringify(
        {
          user_message: "Hello, this is a test message",
          currentDesignChallenge: "Test challenge for mobile app design",
          conversation_history: [],
        },
        null,
        2,
      ),
      "/api/autonomous-ai/autonomous-analysis": JSON.stringify(
        {
          design_challenge: "Test design challenge for mobile app",
          agentId: "DESIGN_LEAD",
          analysis_type: "deep_contextual",
          use_csv_as_knowledge: false,
        },
        null,
        2,
      ),
      "/api/ai/compute-similarity-scores": JSON.stringify(
        {
          design_challenge: "test mobile app design challenge",
          currentDesignChallenge: "test mobile app design challenge",
        },
        null,
        2,
      ),
      "/api/ai/activity-details/1": JSON.stringify(
        {
          design_challenge: "Test challenge",
        },
        null,
        2,
      ),
      "/api/ai/generate": JSON.stringify(
        {
          prompt: "Generate test content",
        },
        null,
        2,
      ),
      "/api/ai/generate-goal": JSON.stringify(
        {
          designChallenge: "Test challenge",
          goalStatement: "Test goal",
          programName: "Test Program",
          programDescription: "Test description",
          programOutcome: "Test outcome",
          tools: "Test tools",
        },
        null,
        2,
      ),
      "/api/ai/generate-expectedDeliverables": JSON.stringify(
        {
          designChallenge: "Test challenge",
          programName: "Test Program",
          tools: [
            { name: "Test Tool", duration: 2, description: "Test description" },
          ],
        },
        null,
        2,
      ),
      "/api/ai/generate-stakeholdersInvolvement": JSON.stringify(
        {
          designChallenge: "Test challenge",
          programName: "Test Program",
          tools: [{ name: "Test Tool", duration: 2 }],
          duration: 8,
        },
        null,
        2,
      ),
      "/api/ai/generate-totalstakeholdersInvolvement": JSON.stringify(
        {
          programs: [
            {
              programName: "Test",
              tools: [{ name: "Tool", duration: 2 }],
              duration: 8,
            },
          ],
        },
        null,
        2,
      ),
      "/api/ai/generate-metricsRecommendation": JSON.stringify(
        {
          designChallenge: "Test challenge",
          shortSummary: "Test summary",
          goalStatement: "Test goal",
        },
        null,
        2,
      ),
      "/api/ai/generate-deliverablesRecommendation": JSON.stringify(
        {
          designChallenge: "Test challenge",
          finalProgramSequence: [],
        },
        null,
        2,
      ),
      "/api/ai/generate-sow": JSON.stringify(
        {
          currentDesignChallenge: "Test challenge",
          shortSummary: "Test summary",
        },
        null,
        2,
      ),
      "/api/ai/activity": JSON.stringify(
        {
          activity_name: "Test activity",
        },
        null,
        2,
      ),
      // Node.js endpoints (keep /api/ prefix - they go through Node.js proxy)
      "/api/user/demo-status": "",
      "/api/user/details": "",
      "/api/save-conversation": JSON.stringify(
        {
          messages: [
            { role: "user", content: "Test message" },
            { role: "assistant", content: "Test response" }
          ],
          plan_id: null,
          title: "Test Conversation"
        },
        null,
        2,
      ),
      "/api/get-conversations": "",
      "/api/plans": "",
    }),
    [],
  );

  const endpoints = useMemo(
    () => [
      // AI endpoints (NOW handled by Node.js at /api/ai/ prefix)
      { url: "/api/ai/ping", method: "GET", type: "AI Health" },
      { url: "/api/ai/echo", method: "POST", type: "AI Echo Test" },
      { url: "/api/chat", method: "POST", type: "AI Chat" },
      { url: "/api/autonomous-ai/autonomous-analysis", method: "POST", type: "AI Analysis (Consolidated)" },
      {
        url: "/api/ai/compute-similarity-scores",
        method: "POST",
        type: "AI Similarity",
      },
      { url: "/api/ai/activity", method: "POST", type: "AI Activity" },
      { url: "/api/ai/generate", method: "POST", type: "AI Generate" },
      { url: "/api/ai/generate-goal", method: "POST", type: "AI Mission: Goal" },
      {
        url: "/api/ai/generate-expectedDeliverables",
        method: "POST",
        type: "AI Mission: Deliverables",
      },
      {
        url: "/api/ai/generate-stakeholdersInvolvement",
        method: "POST",
        type: "AI Mission: Stakeholders",
      },
      {
        url: "/api/ai/generate-totalstakeholdersInvolvement",
        method: "POST",
        type: "AI Mission: Total Stakeholders",
      },
      {
        url: "/api/ai/generate-metricsRecommendation",
        method: "POST",
        type: "AI Mission: Metrics",
      },
      {
        url: "/api/ai/generate-deliverablesRecommendation",
        method: "POST",
        type: "AI Mission: Deliverables Rec",
      },
      { url: "/api/ai/generate-sow", method: "POST", type: "AI Mission: SOW" },
      {
        url: "/api/ai/activity-details/1",
        method: "POST",
        type: "AI Activity Details",
      },
      // Node.js endpoints (keep /api/ prefix - go through Node.js proxy)
      { url: "/api/user/demo-status", method: "GET", type: "User Demo Status" },
      { url: "/api/user/details", method: "GET", type: "Node.js" },
      { url: "/api/save-conversation", method: "POST", type: "Node.js" },
      { url: "/api/get-conversations", method: "GET", type: "Node.js" },
      { url: "/api/plans", method: "GET", type: "Node.js" },
    ],
    [],
  );

  useEffect(() => {
    // Update request body when endpoint changes
    if (testData[selectedEndpoint]) {
      setRequestBody(testData[selectedEndpoint]);
    }

    // Update method based on endpoint
    const endpoint = endpoints.find((ep) => ep.url === selectedEndpoint);
    if (endpoint) {
      setMethod(endpoint.method);
    }
  }, [selectedEndpoint, testData, endpoints]);

  const addResult = (result) => {
    setResults((prev) => [
      {
        ...result,
        timestamp: new Date().toLocaleTimeString(),
        id: Date.now(),
      },
      ...prev,
    ]);
  };

  const testEndpoint = async () => {
    setIsLoading(true);
    console.log(`\n=== TESTING ENDPOINT: ${selectedEndpoint} ===`);
    console.log(`Method: ${method}`);
    console.log(`Request body length: ${requestBody?.length || 0} chars`);

    const startTime = Date.now();

    try {
      const options = {
        method: method,
        headers: {
          "Content-Type": "application/json",
        },
      };

      // Add body for POST requests
      if (method === "POST" && requestBody.trim()) {
        try {
          const parsedBody = JSON.parse(requestBody);
          options.body = JSON.stringify(parsedBody);
          console.log(
            `Request body parsed successfully:`,
            Object.keys(parsedBody),
          );
        } catch (e) {
          console.error(`JSON parsing error:`, e);
          addResult({
            endpoint: selectedEndpoint,
            method: method,
            status: "ERROR",
            error: "Invalid JSON in request body",
            duration: 0,
            debugInfo: `JSON Error: ${e.message}`,
          });
          setIsLoading(false);
          return;
        }
      }

      console.log(`Calling fetchWithAuth with options:`, {
        method: options.method,
        hasBody: !!options.body,
        headers: options.headers,
      });

      // Add timeout to prevent hanging requests
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(
          () => reject(new Error("Request timeout after 30 seconds")),
          30000,
        );
      });

      const response = await Promise.race([
        fetchWithAuth(selectedEndpoint, options),
        timeoutPromise,
      ]);
      const duration = Date.now() - startTime;

      console.log(`Response received after ${duration}ms`);

      if (!response) {
        console.error(`fetchWithAuth returned null for ${selectedEndpoint}`);
        addResult({
          endpoint: selectedEndpoint,
          method: method,
          status: "ERROR",
          error: "fetchWithAuth returned null",
          duration: duration,
          debugInfo:
            "fetchWithAuth function returned null - check network or proxy issues",
        });
        return;
      }

      // Check for proxy routing issues
      if (response.status >= 400) {
        const responseText = await response.text();
        if (
          responseText.includes("Proxy error") ||
          responseText.includes("ECONNRESET") ||
          responseText.includes("400 Bad Request") ||
          responseText.includes("could not understand")
        ) {
          addResult({
            endpoint: selectedEndpoint,
            method: method,
            status: response.status,
            ok: false,
            error: "Proxy routing issue detected",
            duration: duration,
            debugInfo: {
              proxyError: responseText,
              suggestion:
                "Node.js proxy is not routing requests to correct Python endpoints. Check server.js proxy configuration.",
              expectedPath: selectedEndpoint.replace("/api", ""),
              actualPythonPath:
                "Check Python server logs for actual path received",
              commonIssue:
                "All requests may be routing to Python / instead of specific endpoints",
            },
          });
          return;
        }
      }

      const contentType = response.headers.get("content-type");
      
      // Special check for successful responses that might indicate routing issues
      if (
        response.ok &&
        contentType &&
        contentType.includes("application/json")
      ) {
        try {
          const responseData = await response.json();

          // Check if we got the Python root response when we expected a specific endpoint
          if (
            responseData.available_endpoints &&
            selectedEndpoint !== "/api" &&
            selectedEndpoint !== "/api/"
          ) {
            // Log all response headers for debugging
            const responseHeaders = {};
            response.headers.forEach((value, key) => {
              responseHeaders[key] = value;
            });

            addResult({
              endpoint: selectedEndpoint,
              method: method,
              status: response.status,
              ok: false,
              error:
                "🚨 PROXY ROUTING FAILURE: All requests hitting Python root / instead of specific endpoints",
              duration: duration,
              debugInfo: {
                CRITICAL_ISSUE: "The pathRewrite function in server.js is not working correctly",
                requestedEndpoint: selectedEndpoint,
                expectedPythonPath: selectedEndpoint.replace("/api", ""),
                actualPythonPath: "/",
                pythonResponse: responseData,
                SOLUTION: "Check the pathRewrite function in server.js - it should transform '/api/generate-goal' to '/generate-goal' but it's transforming everything to '/'",
                CHECK_LOGS: "Look for 'Path rewrite:' messages in Node.js server console",
              },
            });
            return;
          }

          // Log all response headers for debugging
          const responseHeaders = {};
          response.headers.forEach((value, key) => {
            responseHeaders[key] = value;
          });

          // Continue with normal processing
          addResult({
            endpoint: selectedEndpoint,
            method: method,
            status: response.status,
            ok: response.ok,
            contentType: contentType,
            data: responseData,
            duration: duration,
            debugInfo: {
              responseUrl: response.url,
              parseError: null,
              headers: responseHeaders,
              bodySize: options.body?.length || 0,
            },
          });
          return;
        } catch (parseError) {
          // Fall through to normal text processing
        }
      }

      console.log(`Response status: ${response.status}`);
      console.log(`Response ok: ${response.ok}`);
      console.log(`Response URL: ${response.url}`);
      console.log(`Content-Type: ${contentType}`);
      
      // Log all response headers for debugging
      const responseHeaders = {};
      response.headers.forEach((value, key) => {
        responseHeaders[key] = value;
      });
      console.log(`All response headers:`, responseHeaders);

      let responseData;
      let parseError = null;

      try {
        if (contentType && contentType.includes("application/json")) {
          responseData = await response.json();
          console.log(`JSON response parsed successfully`);
        } else {
          responseData = await response.text();
          console.log(`Text response received, length: ${responseData.length}`);
        }
      } catch (error) {
        parseError = error.message;
        responseData = "Failed to parse response";
        console.error(`Response parsing error:`, error);
      }

      // Log response data preview
      if (typeof responseData === "object") {
        console.log(`Response data keys:`, Object.keys(responseData));
      } else {
        console.log(`Response data preview:`, responseData?.substring(0, 200));
      }

      addResult({
        endpoint: selectedEndpoint,
        method: method,
        status: response.status,
        ok: response.ok,
        contentType: contentType,
        data: responseData,
        duration: duration,
        debugInfo: {
          responseUrl: response.url,
          parseError: parseError,
          headers: responseHeaders,
          bodySize: options.body?.length || 0,
        },
      });
    } catch (error) {
      const duration = Date.now() - startTime;
      console.error(`Exception during test:`, error);
      addResult({
        endpoint: selectedEndpoint,
        method: method,
        status: "ERROR",
        error: error.message,
        duration: duration,
        debugInfo: {
          errorType: error.constructor.name,
          errorStack: error.stack?.split("\n")[0],
        },
      });
    } finally {
      setIsLoading(false);
      console.log(`=== END TEST ===\n`);
    }
  };

  const testAllEndpoints = async () => {
    setIsLoading(true);
    console.log("Testing all endpoints...");

    for (let i = 0; i < endpoints.length; i++) {
      const endpoint = endpoints[i];
      // Progressive delay to prevent conflicts (AI endpoints need more time)
      const delay = endpoint.type.includes("AI") ? 1000 + i * 200 : 500;
      await new Promise((resolve) => setTimeout(resolve, delay));

      const startTime = Date.now();
      try {
        const options = {
          method: endpoint.method,
          headers: {
            "Content-Type": "application/json",
          },
        };

        // Add test data for POST requests
        if (endpoint.method === "POST" && testData[endpoint.url]) {
          options.body = testData[endpoint.url];
        }

        const response = await fetchWithAuth(endpoint.url, options);
        const duration = Date.now() - startTime;

        if (!response) {
          addResult({
            endpoint: endpoint.url,
            method: endpoint.method,
            status: "ERROR",
            error: "fetchWithAuth returned null",
            duration: duration,
            type: endpoint.type,
          });
          continue;
        }

        const contentType = response.headers.get("content-type");
        let responseData;

        try {
          if (contentType && contentType.includes("application/json")) {
            responseData = await response.json();
          } else {
            responseData = await response.text();
          }
        } catch (parseError) {
          responseData = "Failed to parse response";
        }

        addResult({
          endpoint: endpoint.url,
          method: endpoint.method,
          status: response.status,
          ok: response.ok,
          contentType: contentType,
          data: responseData,
          duration: duration,
          type: endpoint.type,
        });
      } catch (error) {
        const duration = Date.now() - startTime;
        addResult({
          endpoint: endpoint.url,
          method: endpoint.method,
          status: "ERROR",
          error: error.message,
          duration: duration,
          type: endpoint.type,
        });
      }
    }

    setIsLoading(false);
  };

  const testPythonDirect = async () => {
    console.log("Testing Python server directly...");
    const startTime = Date.now();

    try {
      // Test direct connection to Python server using environment variable
      const pythonApiUrl = process.env.REACT_APP_PYTHON_API_URL || 'http://127.0.0.1:5001';
      const response = await fetch(`${pythonApiUrl}/ping`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });
      const duration = Date.now() - startTime;

      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        data = await response.text();
      }

      console.log("Direct Python test result:", data);

      addResult({
        endpoint: "/ping",
        method: "GET",
        status: response.status,
        ok: response.ok,
        data: data,
        duration: duration,
        type: "Direct Python",
      });
    } catch (error) {
      const duration = Date.now() - startTime;
      console.error("Direct Python test failed:", error);
      addResult({
        endpoint: "/ping",
        method: "GET",
        status: "ERROR",
        error: error.message,
        duration: duration,
        type: "Direct Python",
      });
    }
  };

  const testProxyRouting = async () => {
    console.log("Testing proxy routing specifically...");

    // Test endpoints that should route differently
    const routingTests = [
      {
        endpoint: "/api/ping",
        expectedPython: "/ping",
        type: "Python Health",
        method: "GET",
      },
      {
        endpoint: "/api/chat",
        expectedPython: "/chat",
        type: "Python AI",
        method: "POST",
        body: { user_message: "test", conversation_history: [] },
      },
      {
        endpoint: "/api/analyze",
        expectedPython: "/analyze",
        type: "Python AI",
        method: "POST",
        body: { design_challenge: "test" },
      },
      {
        endpoint: "/api/user/details",
        expectedPython: "Node.js only",
        type: "Node.js",
        method: "GET",
      },
    ];

    for (const test of routingTests) {
      const startTime = Date.now();
      try {
        console.log(`Testing routing for: ${test.endpoint} (${test.method})`);
        const options = {
          method: test.method,
          headers: { "Content-Type": "application/json" },
        };

        if (test.body && test.method === "POST") {
          options.body = JSON.stringify(test.body);
        }

        const response = await fetchWithAuth(test.endpoint, options);

        const duration = Date.now() - startTime;
        let responseData;
        try {
          responseData = await response.json();
        } catch {
          responseData = await response.text();
        }

        // Check if this should be a Python endpoint but got routed to root
        let routingIssue = null;
        if (
          test.expectedPython !== "Node.js only" &&
          responseData &&
          responseData.available_endpoints &&
          responseData.message === "Python API server is running"
        ) {
          routingIssue = `❌ ROUTING ISSUE: ${test.endpoint} was routed to Python / instead of ${test.expectedPython}`;
        }

        addResult({
          endpoint: test.endpoint,
          method: "GET",
          status: response.status,
          ok: response.ok && !routingIssue,
          data: responseData,
          duration: duration,
          type: `Routing Test - ${test.type}`,
          error: routingIssue,
          debugInfo: {
            expectedPythonPath: test.expectedPython,
            routingResult: routingIssue ? "FAILED - Routed to /" : "SUCCESS",
            serverLogCheck:
              'Check Node.js server logs for "Path rewrite" messages',
            pythonLogCheck: "Check Python server logs for actual path received",
          },
        });
      } catch (error) {
        const duration = Date.now() - startTime;
        addResult({
          endpoint: test.endpoint,
          method: "GET",
          status: "ERROR",
          error: error.message,
          duration: duration,
          type: `Routing Test - ${test.type}`,
        });
      }

      // Small delay between tests
      await new Promise((resolve) => setTimeout(resolve, 200));
    }
  };

  const clearResults = () => setResults([]);

  return (
    <Box p={6} maxW="1200px" mx="auto">
      <VStack spacing={6} align="stretch">
        <Box>
          <Text fontSize="2xl" fontWeight="bold" mb={4}>
            API Endpoint Tester
          </Text>
          <Text fontSize="sm" color="gray.600" mb={2}>
            Test AI endpoints and Node.js routes to debug proxy routing issues.
            AI endpoints go through Python server.
          </Text>
          <Alert status="info" size="sm">
            <AlertIcon />
            <Text fontSize="xs">
              <strong>Testing Info:</strong> AI endpoints are routed through the
              Python server on port 5001. Node.js endpoints are handled directly
              on port 5000. Check the console for detailed request/response
              logs.
            </Text>
          </Alert>
        </Box>

        {/* Single Endpoint Test */}
        <Box p={4} border="1px" borderColor="gray.200" borderRadius="md">
          <Text fontSize="lg" fontWeight="semibold" mb={3}>
            Single Endpoint Test
          </Text>

          <VStack spacing={3} align="stretch">
            <HStack>
              <Select
                value={selectedEndpoint}
                onChange={(e) => setSelectedEndpoint(e.target.value)}
                maxW="300px"
              >
                {endpoints.map((ep) => (
                  <option key={ep.url} value={ep.url}>
                    {ep.method} {ep.url} ({ep.type})
                  </option>
                ))}
              </Select>

              <Input
                value={method}
                onChange={(e) => setMethod(e.target.value)}
                maxW="100px"
                isReadOnly
              />
            </HStack>

            {method === "POST" && (
              <Box>
                <Text fontSize="sm" mb={2}>
                  Request Body (JSON):
                </Text>
                <Textarea
                  value={requestBody}
                  onChange={(e) => setRequestBody(e.target.value)}
                  placeholder="Enter JSON request body..."
                  minH="120px"
                  fontFamily="mono"
                  fontSize="sm"
                />
              </Box>
            )}

            <HStack>
              <Button
                onClick={testEndpoint}
                isLoading={isLoading}
                colorScheme="blue"
              >
                Test Endpoint
              </Button>

              <Button
                onClick={testAllEndpoints}
                isLoading={isLoading}
                colorScheme="green"
              >
                Test All Endpoints
              </Button>

              <Button onClick={testPythonDirect} colorScheme="purple">
                Test Python Direct
              </Button>

              <Button onClick={testProxyRouting} colorScheme="orange">
                Test Proxy Routing
              </Button>

              <Button
                onClick={() => {
                  console.log("=== PROXY DEBUGGING INSTRUCTIONS ===");
                  console.log(
                    '1. Check Node.js server logs for "Path rewrite" messages',
                  );
                  console.log(
                    '2. Check Python server logs for "Path:" to see actual received paths',
                  );
                  console.log(
                    "3. Expected: /api/ping should rewrite to /ping and hit Python /ping endpoint",
                  );
                  console.log(
                    "4. Current issue: All paths are being rewritten to / and hitting Python root",
                  );
                  console.log("5. Look for pathRewrite function in server.js");
                }}
                colorScheme="purple"
                size="sm"
              >
                Debug Info
              </Button>

              <Button onClick={clearResults} variant="outline">
                Clear Results
              </Button>
            </HStack>
          </VStack>
        </Box>

        {/* Results */}
        <Box>
          <Text fontSize="lg" fontWeight="semibold" mb={3}>
            Test Results ({results.length})
          </Text>

          {results.length === 0 ? (
            <Alert status="info">
              <AlertIcon />
              No test results yet. Run a test to see results here.
            </Alert>
          ) : (
            <VStack spacing={3} align="stretch">
              {results.map((result) => (
                <Box
                  key={result.id}
                  p={4}
                  border="1px"
                  borderColor={result.ok ? "green.200" : "red.200"}
                  borderRadius="md"
                  bg={result.ok ? "green.50" : "red.50"}
                >
                  <HStack justify="space-between" mb={2}>
                    <HStack>
                      <Badge colorScheme={result.ok ? "green" : "red"}>
                        {result.method}
                      </Badge>
                      <Text fontWeight="semibold">{result.endpoint}</Text>
                      {result.type && (
                        <Badge variant="outline">{result.type}</Badge>
                      )}
                    </HStack>
                    <HStack>
                      <Text fontSize="sm" color="gray.600">
                        {result.timestamp}
                      </Text>
                      <Badge>{result.duration}ms</Badge>
                      <Badge colorScheme={result.ok ? "green" : "red"}>
                        {result.status}
                      </Badge>
                    </HStack>
                  </HStack>

                  {result.error && (
                    <Alert status="error" size="sm" mb={2}>
                      <AlertIcon />
                      <Text fontSize="sm">{result.error}</Text>
                    </Alert>
                  )}

                  {result.debugInfo && (
                    <Box
                      mb={2}
                      p={2}
                      bg="blue.50"
                      borderRadius="md"
                      fontSize="xs"
                    >
                      <Text fontWeight="bold" mb={1}>
                        Debug Info:
                      </Text>
                      <Code display="block" whiteSpace="pre-wrap">
                        {typeof result.debugInfo === "object"
                          ? JSON.stringify(result.debugInfo, null, 2)
                          : result.debugInfo}
                      </Code>
                    </Box>
                  )}

                  {result.contentType && (
                    <Text fontSize="xs" color="gray.600" mb={2}>
                      Content-Type: {result.contentType}
                    </Text>
                  )}

                  {result.data && (
                    <Box>
                      <Text fontSize="sm" fontWeight="semibold" mb={1}>
                        Response:
                      </Text>
                      <Code
                        p={2}
                        fontSize="xs"
                        maxH="200px"
                        overflowY="auto"
                        display="block"
                        whiteSpace="pre-wrap"
                      >
                        {typeof result.data === "string"
                          ? result.data
                          : JSON.stringify(result.data, null, 2)}
                      </Code>
                    </Box>
                  )}
                </Box>
              ))}
            </VStack>
          )}
        </Box>
      </VStack>
    </Box>
  );
};

export default EndpointTester;
